import { useCallback, useEffect, useRef, useState } from "react";
import {
  buildRecipientListDropdownFilter,
  EMAIL_DISTRIBUTION_LIST_DROPDOWN_FIELDS,
  EMAIL_DISTRIBUTION_LIST_PAGE_SIZE,
  getEmailDistributionLists,
} from "../../Services/EmailDistributionList";
import { getMatchingBuiltinRecipientListOptions } from "./builtinRecipientLists";

export type RecipientListDropdownOption = {
  id: string;
  name: string;
  emailTemplateIds?: string[];
};

export type RecipientListDropdownScope = "template" | "all";

const SEARCH_DEBOUNCE_MS = 300;

const dedupeOptions = (
  options: RecipientListDropdownOption[],
): RecipientListDropdownOption[] => {
  const seen = new Set<string>();
  return options.filter((option) => {
    if (!option.id || seen.has(option.id)) {
      return false;
    }
    seen.add(option.id);
    return true;
  });
};

interface UsePaginatedEmailDistributionListsOptions {
  enabled?: boolean;
  isOpen?: boolean;
  /** Email template # used when scope is `template`. */
  emailTemplateNumber?: string;
}

export function usePaginatedEmailDistributionLists({
  enabled = true,
  isOpen = false,
  emailTemplateNumber = "",
}: UsePaginatedEmailDistributionListsOptions = {}) {
  const [items, setItems] = useState<RecipientListDropdownOption[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [pageIndex, setPageIndex] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [listScope, setListScope] =
    useState<RecipientListDropdownScope>("template");
  const requestIdRef = useRef(0);

  const trimmedTemplateNumber = emailTemplateNumber.trim();
  const showViewMore = listScope === "template" && !!trimmedTemplateNumber;

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearchQuery(searchQuery.trim());
    }, SEARCH_DEBOUNCE_MS);

    return () => window.clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    setListScope("template");
  }, [trimmedTemplateNumber, enabled]);

  useEffect(() => {
    if (!isOpen) {
      setListScope("template");
    }
  }, [isOpen]);

  useEffect(() => {
    setPageIndex(0);
    setItems([]);
    setHasMore(false);
  }, [debouncedSearchQuery, enabled, trimmedTemplateNumber, listScope]);

  const fetchPage = useCallback(
    async (targetPageIndex: number, append: boolean) => {
      if (!enabled) {
        return;
      }

      const requestId = ++requestIdRef.current;
      if (append) {
        setIsLoadingMore(true);
      } else {
        setIsLoading(true);
      }

      try {
        const filterTemplateNumber =
          listScope === "template" ? trimmedTemplateNumber : undefined;

        const lists = await getEmailDistributionLists({
          filter: buildRecipientListDropdownFilter(
            debouncedSearchQuery,
            filterTemplateNumber,
          ),
          fields: [...EMAIL_DISTRIBUTION_LIST_DROPDOWN_FIELDS],
          sort: { field: "name", order: "asc" },
          pagination: {
            pageIndex: targetPageIndex,
            pageSize: EMAIL_DISTRIBUTION_LIST_PAGE_SIZE,
          },
        });

        if (requestId !== requestIdRef.current) {
          return;
        }

        const nextItems = lists
          .filter((item) => !!item.id && !!item.name)
          .map((item) => ({
            id: item.id || "",
            name: item.name,
            emailTemplateIds: item.emailTemplateIds,
          }));

        const includeBuiltin = !append && !debouncedSearchQuery;
        const builtinItems = includeBuiltin
          ? getMatchingBuiltinRecipientListOptions(debouncedSearchQuery)
          : [];

        setItems((prev) =>
          append
            ? dedupeOptions([...prev, ...nextItems])
            : dedupeOptions([...builtinItems, ...nextItems]),
        );
        setHasMore(nextItems.length === EMAIL_DISTRIBUTION_LIST_PAGE_SIZE);
        setPageIndex(targetPageIndex);
      } finally {
        if (requestId === requestIdRef.current) {
          setIsLoading(false);
          setIsLoadingMore(false);
        }
      }
    },
    [debouncedSearchQuery, enabled, listScope, trimmedTemplateNumber],
  );

  useEffect(() => {
    if (!enabled || !isOpen) {
      return;
    }
    fetchPage(0, false);
  }, [enabled, isOpen, debouncedSearchQuery, listScope, fetchPage]);

  const loadMore = useCallback(() => {
    if (!enabled || isLoading || isLoadingMore || !hasMore) {
      return;
    }
    fetchPage(pageIndex + 1, true);
  }, [enabled, fetchPage, hasMore, isLoading, isLoadingMore, pageIndex]);

  const expandToAllLists = useCallback(() => {
    if (listScope === "all") {
      return;
    }
    setListScope("all");
  }, [listScope]);

  const prependItem = useCallback((item: RecipientListDropdownOption) => {
    if (!item.id) {
      return;
    }
    setItems((prev) => dedupeOptions([item, ...prev]));
  }, []);

  const updateItemEmailTemplateIds = useCallback(
    (listId: string, emailTemplateIds: string[]) => {
      if (!listId) {
        return;
      }
      setItems((prev) =>
        prev.map((item) =>
          item.id === listId ? { ...item, emailTemplateIds } : item,
        ),
      );
    },
    [],
  );

  const reset = useCallback(() => {
    requestIdRef.current += 1;
    setSearchQuery("");
    setDebouncedSearchQuery("");
    setPageIndex(0);
    setItems([]);
    setHasMore(false);
    setIsLoading(false);
    setIsLoadingMore(false);
    setListScope("template");
  }, []);

  return {
    items,
    searchQuery,
    setSearchQuery,
    hasMore,
    isLoading,
    isLoadingMore,
    loadMore,
    prependItem,
    updateItemEmailTemplateIds,
    reset,
    showViewMore,
    expandToAllLists,
    listScope,
  };
}
